/* eslint-disable no-shadow */
import React, { useContext, useEffect, useState } from "react";

import { isEqual, omit } from "lodash";
import { useRouter } from "next/router";
import Select from "react-select";
import styled from "styled-components";
import { useDebounce } from "use-debounce";

import VaspList from "../VaspList";
import {
  ACTIVITY,
  BADGES,
  DEFAULT_FILTERS,
  DUE_DILIGENCE_REVIEW_STATUS,
  JURISDICTION_STATUS,
  FiltersConfig,
  getAllJurisdictionsOptions,
  REGULATORY_STATUS,
  replaceQueryFilters,
  SORT,
} from "./util";

import { trackAction } from "~/analytics";
import { Padding } from "~/components/Elements";
import { Control, LabelSub } from "~/components/Form";
import Pagination from "~/components/Pagination";
import VASPListSubsidiaries from "~/components/VASPListSubsidiaries";
import { BackButtonContext } from "~/contexts/BackButtonContext/BackButtonContext";
import countries from "~/data/countries";
import { useTfSimpleVaspList } from "~/endpoints/trustFramework";
import { SimpleListVASPInput } from "~/endpoints/trustFramework/types";
import useCurrentViewer from "~/hooks/useCurrentViewer";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import usePrevious from "~/hooks/usePrevious";
import { filterBySelectCustomStyles, sortBySelectCustomStyles } from "~/styles/theme";
import { JurisdictionStatus, VASP } from "~/types/vaspid";

export interface VaspsNetworkProps {
  onClickVasp: (vasp: VASP) => void;
  trackingActionCategory?: string;
  includeVaspReview?: boolean;
  children: (params: {
    term: string;
    setTerm: (newSearchQuery: string) => void;
    sortFilterContent?: JSX.Element;
    filtersContent?: JSX.Element;
    resultsContent: JSX.Element | null;
  }) => JSX.Element;
}

const SelectControl = styled(Control)`
  div[class$="-control"] {
    height: 47px;
  }
`;

export const NoResultsMessage = (): JSX.Element => (
  <p>No match found. Please try again using different keywords or modifying the filters.</p>
);

export default function VaspsNetwork({
  onClickVasp,
  children,
  trackingActionCategory,
  includeVaspReview = false,
}: VaspsNetworkProps) {
  const router = useRouter();
  const [filtersConfig, setFiltersConfig] = useState<FiltersConfig | undefined>();
  const prevFiltersConfig = usePrevious<FiltersConfig | undefined>(filtersConfig);

  const [currentPage, setCurrentPage] = useState<string>("");
  const [term, setTerm] = useState<string | undefined>(undefined);
  const [termDebounced] = useDebounce(term, 500);
  const prevTerm = usePrevious(termDebounced);
  const [pulloutSubsidiaries, setPulloutSubsidiaries] = useState<boolean>(false);

  const { viewer: currentViewer } = useCurrentViewer();
  const isLoggedIn = currentViewer.did !== "noUser";
  const vaspDID = useCurrentViewerVaspDID();

  const { subsidiariesOf: gatewayVaspDid } = router.query;

  const { storeSearchParams } = useContext(BackButtonContext);

  useEffect(() => {
    /**
     * Set URL to contain only the term, as pagination makes no sense anymore. Filters remain active in URL.
     */
    if (prevTerm === undefined) {
      return;
    }

    const newRouterQuery = {
      ...omit(router.query, ["currentPage", "subsidiariesOf"]),
      q: termDebounced,
    };
    trackAction(`user-changed-${trackingActionCategory}-term`, { prev: prevTerm || "", next: termDebounced || "" });
    router.replace({ pathname: router.pathname, query: newRouterQuery }, undefined, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termDebounced]);

  const { dataVASPS, pagination, isLoading, showPagination } = useAllVASPs(
    termDebounced || "",
    Number(currentPage),
    pulloutSubsidiaries,
    filtersConfig,
    includeVaspReview ? vaspDID : undefined,
  );

  const setFilterProp = (key: keyof FiltersConfig, value?: string, label?: string) => {
    if (!value) {
      return;
    }
    if (!filtersConfig || prevFiltersConfig === undefined) {
      return;
    }
    const nextFiltersConfig: FiltersConfig = { ...filtersConfig, [key]: value };
    if (isEqual(prevFiltersConfig, nextFiltersConfig)) {
      return;
    }

    trackAction(`user-changed-${trackingActionCategory}-filters-diff`, {
      name: key,
      next: label ?? "",
    });
    setFiltersConfig(nextFiltersConfig);
  };

  useEffect(() => {
    /**
     * Set the initial configuration for the filters (default values + query) when the component is mounted,
     * so the router is available.
     */
    const initialFiltersConfig = Object.keys(DEFAULT_FILTERS).reduce((prev, k) => {
      const override = router.query[k];
      return override ? { ...prev, [k]: override } : prev;
    }, DEFAULT_FILTERS);
    setFiltersConfig(initialFiltersConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filtersConfig) {
      return;
    }
    setPulloutSubsidiaries(!isEqual(omit(DEFAULT_FILTERS, ["sort"]), omit(filtersConfig, ["sort"])));
    replaceQueryFilters({ router, filtersConfig, clearResults: prevFiltersConfig !== undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersConfig]);

  useEffect(() => {
    setTerm(String(router.query.q || ""));
  }, [router.query.q]);

  useEffect(() => {
    document.getElementById("vasp_search_top")?.scrollIntoView({ behavior: "auto" });
    setCurrentPage(String(router.query.currentPage || 0));
  }, [router.query.currentPage, router.query.subsidiariesOf]);

  const sortFilterContent = (
    <div
      style={{
        display: "flex",
        columnGap: "10px",
        alignItems: "center",
      }}
    >
      <LabelSub
        style={{
          fontWeight: "bold",
          textTransform: "uppercase",
          fontSize: "12px",
          whiteSpace: "nowrap",
          width: "60px",
          textAlign: "right",
        }}
        transform={null}
        weight="normal"
      >
        {"Sort By"}
      </LabelSub>

      <SelectControl>
        <Select
          isSearchable={false}
          isModified={filtersConfig?.sort !== DEFAULT_FILTERS.sort}
          styles={sortBySelectCustomStyles}
          components={{ IndicatorSeparator: () => null }}
          placeholder="SORT BY"
          instanceId={"select"}
          onChange={(event) => setFilterProp("sort", event?.value, event?.label)}
          options={SORT}
          value={SORT.find((option) => option.value === filtersConfig?.sort)}
        />
      </SelectControl>
    </div>
  );

  const filtersContent = (
    <div
      style={{
        display: "flex",
        columnGap: "10px",
        alignItems: "center",
      }}
    >
      <LabelSub
        style={{
          fontWeight: "bold",
          textTransform: "uppercase",
          fontSize: "12px",
          whiteSpace: "nowrap",
          width: "60px",
          textAlign: "right",
        }}
        transform={null}
        weight="normal"
      >
        {"Filter By"}
      </LabelSub>

      <div style={{ display: "flex", flexWrap: "wrap", columnGap: "10px", rowGap: "5px" }}>
        <SelectControl>
          <Select
            isSearchable={false}
            isModified={filtersConfig?.activity !== DEFAULT_FILTERS.activity}
            styles={filterBySelectCustomStyles}
            components={{ IndicatorSeparator: () => null }}
            instanceId={"select"}
            onChange={(event) => setFilterProp("activity", event?.value, event?.label)}
            options={ACTIVITY}
            value={ACTIVITY.find((option) => option.value === filtersConfig?.activity)}
          />
        </SelectControl>

        {isLoggedIn && (
          <SelectControl>
            <Select
              isSearchable={false}
              isModified={filtersConfig?.regulatory_status !== DEFAULT_FILTERS.regulatory_status}
              styles={filterBySelectCustomStyles}
              components={{ IndicatorSeparator: () => null }}
              instanceId={"select"}
              onChange={(event) => setFilterProp("regulatory_status", event?.value, event?.label)}
              options={REGULATORY_STATUS}
              value={REGULATORY_STATUS.find((option) => option.value === filtersConfig?.regulatory_status)}
            />
          </SelectControl>
        )}

        <SelectControl>
          <Select
            isSearchable={false}
            isModified={filtersConfig?.badges !== DEFAULT_FILTERS.badges}
            styles={filterBySelectCustomStyles}
            components={{ IndicatorSeparator: () => null }}
            instanceId={"select"}
            onChange={(event) => setFilterProp("badges", event?.value, event?.label)}
            options={BADGES}
            value={BADGES.find((option) => option.value === filtersConfig?.badges)}
          />
        </SelectControl>

        {isLoggedIn && includeVaspReview && (
          <SelectControl>
            <Select
              isSearchable={false}
              isModified={filtersConfig?.review_status !== DEFAULT_FILTERS.review_status}
              styles={filterBySelectCustomStyles}
              components={{ IndicatorSeparator: () => null }}
              instanceId={"select"}
              onChange={(event) => setFilterProp("review_status", event?.value, event?.label)}
              options={DUE_DILIGENCE_REVIEW_STATUS}
              value={DUE_DILIGENCE_REVIEW_STATUS.find((option) => option.value === filtersConfig?.review_status)}
            />
          </SelectControl>
        )}

        {filtersConfig?.jurisdiction_status === "all_jurisdiction_status" && (
          <SelectControl>
            <Select
              isSearchable
              isModified={filtersConfig?.jurisdictions !== DEFAULT_FILTERS.jurisdictions}
              styles={filterBySelectCustomStyles}
              components={{ IndicatorSeparator: () => null }}
              instanceId={"select"}
              onChange={(event) => setFilterProp("jurisdictions", event?.value, event?.label)}
              options={getAllJurisdictionsOptions(countries)}
              value={getAllJurisdictionsOptions(countries).find(
                (option) => option.value === filtersConfig?.jurisdictions,
              )}
            />
          </SelectControl>
        )}

        {isLoggedIn && filtersConfig?.jurisdictions === "all_jurisdictions" && (
          <SelectControl>
            <Select
              isSearchable={false}
              isModified={filtersConfig?.jurisdiction_status !== DEFAULT_FILTERS.jurisdiction_status}
              styles={filterBySelectCustomStyles}
              components={{ IndicatorSeparator: () => null }}
              instanceId={"select"}
              onChange={(event) => setFilterProp("jurisdiction_status", event?.value, event?.label)}
              options={JURISDICTION_STATUS}
              value={JURISDICTION_STATUS.find((option) => option.value === filtersConfig?.jurisdiction_status)}
            />
          </SelectControl>
        )}
      </div>
    </div>
  );

  const onClickVaspWrapper = (vasp: VASP) => {
    storeSearchParams();
    return onClickVasp(vasp);
  };

  const resultsContent =
    dataVASPS?.vasps && dataVASPS?.vasps.length === 0 ? null : (
      <>
        {gatewayVaspDid ? (
          <VASPListSubsidiaries gatewayVaspDid={String(gatewayVaspDid)} includeVaspReview={includeVaspReview} />
        ) : (
          <VaspList
            handleVaspCardClicked={onClickVaspWrapper}
            vasps={isLoading ? undefined : dataVASPS?.vasps}
            includeGatewayBelonging
            includeVaspReview={includeVaspReview}
          />
        )}
        {!gatewayVaspDid && pagination && showPagination && (
          <Padding sized="medium">
            <Pagination totalPages={pagination.total / pagination.per_page} />
          </Padding>
        )}
      </>
    );

  return children({
    term: term || "",
    setTerm,
    sortFilterContent: gatewayVaspDid ? undefined : sortFilterContent, // intentionally hide filters when viewing a gateway VASP did
    filtersContent: gatewayVaspDid ? undefined : filtersContent,
    resultsContent,
  });
}

function useAllVASPs(
  query: string,
  page: number,
  pulloutSubsidiaries: boolean,
  filtersConfig?: FiltersConfig,
  reviewedByVaspDID?: string,
  filterByJurisdictionStatus?: JurisdictionStatus,
) {
  const reviewerVaspDID = useCurrentViewerVaspDID();
  const fields = [
    "did",
    "name",
    "logo",
    "verificationStatus",
    "jurisdictions",
    "division",
    "subsidiaries",
    "hasAdmin",
    "isNotifiable",
    "isRegulated",
    "parentGateway",
    ...(reviewedByVaspDID ? ["reviewValue", "reviewDate"] : []),
  ];

  const variables: SimpleListVASPInput = {
    per_page: 100,
    page,
    ...(query && { q: query }),
    fields,
    includeUncheckedVASPs: false,
    listingType: pulloutSubsidiaries ? "exclude_gateways" : "exclude_subsidiaries",
    ...(reviewedByVaspDID ? { reviewedByVaspDID } : {}),
    ...(filterByJurisdictionStatus ? { filterByJurisdictionStatus } : {}),
  };

  let order: Array<string>;
  switch (filtersConfig?.sort) {
    case "registration_asc":
      order = ["createdAt:ASC"];
      break;
    case "registration_desc":
      order = ["createdAt:DESC"];
      break;
    case "recommended":
      order = ["recommended:DESC"];
      break;
    case "alphabetical":
    default:
      order = ["name:ASC"];
  }

  if (filtersConfig) {
    if (filtersConfig.activity !== "all_data_transfers") {
      variables.includeActiveSendersOnly = filtersConfig.activity === "sending_data_transfers" ? "true" : "false";
      variables.includeActiveReceiversOnly = filtersConfig.activity === "receiving_data_transfers" ? "true" : "false";
      order.push(
        `${filtersConfig.activity === "sending_data_transfers" ? "lastSentDate" : "lastReceivedDate"}:DESC:NULLS LAST`,
      );
    }

    if (filtersConfig.regulatory_status !== "all_status") {
      variables.regulatoryStatus = filtersConfig.regulatory_status;
    }

    if (filtersConfig.badges !== "all_badges") {
      variables.badge = filtersConfig.badges;
    }

    if (filtersConfig.jurisdictions !== "all_jurisdictions") {
      variables.jurisdictions = filtersConfig.jurisdictions;
    }

    if (filtersConfig.review_status !== "all_review_status") {
      variables.reviewValue = filtersConfig.review_status;
      variables.reviewedByVaspDID = reviewerVaspDID;
    }

    if (filtersConfig.jurisdiction_status !== "all_jurisdiction_status") {
      variables.filterByJurisdictionStatus = filtersConfig.jurisdiction_status;
    }
  }

  variables.order = order.join(",");

  const { data: dataVASPS, isLoading } = useTfSimpleVaspList(variables, !!reviewedByVaspDID);

  const pagination = dataVASPS?.pagination;
  let showPagination = false;
  if (pagination) {
    showPagination = pagination.total > pagination.per_page;
  }

  return {
    dataVASPS,
    pagination,
    isLoading,
    showPagination,
  } as const;
}
